import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Workflow, 
  Bot, 
  Mail, 
  TrendingUp, 
  Users, 
  CheckCircle2, 
  Clock, 
  Zap,
  ArrowRight,
  BarChart3,
  Play,
  Pause,
  RefreshCw,
  Eye,
  Settings,
  Trash2
} from 'lucide-react';
import { Badge } from './ui/badge';
import { AdminAnalyticsDashboard } from './AdminAnalyticsDashboard';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import { OnboardingWizard } from './OnboardingWizard';

const ONBOARDING_KEY = 'zaphub_onboarding_done';

export function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'ADMIN' || (user?.role as string) === 'admin';

  // Show onboarding only once per user
  const [showOnboarding, setShowOnboarding] = useState(
    !localStorage.getItem(ONBOARDING_KEY)
  );

  const handleOnboardingComplete = () => {
    localStorage.setItem(ONBOARDING_KEY, '1');
    setShowOnboarding(false);
  };

  // Estado real para workflows
  const [workflows, setWorkflows] = useState([
    { 
      id: '1', 
      name: 'Envío de Ofertas Diarias', 
      status: 'active', 
      lastRun: '5 min ago',
      executions: 127 
    },
    { 
      id: '2', 
      name: 'Notificación de Tareas Pendientes', 
      status: 'active', 
      lastRun: '1 hour ago',
      executions: 89 
    },
    { 
      id: '3', 
      name: 'Reporte Semanal Automático', 
      status: 'paused', 
      lastRun: '2 days ago',
      executions: 24 
    },
  ]);

  const stats = [
    { label: 'Workflows Activos', value: workflows.filter(w => w.status === 'active').length.toString(), icon: Workflow, color: 'text-primary', action: () => navigate('/dashboard/workflows') },
    { label: 'Agentes IA', value: '5', icon: Bot, color: 'text-primary', action: () => navigate('/dashboard/agents') },
    { label: 'Ejecuciones Hoy', value: '284', icon: TrendingUp, color: 'text-primary', action: () => navigate('/dashboard/analytics') },
    { label: 'Tiempo Ahorrado', value: '18h', icon: Clock, color: 'text-primary', action: () => navigate('/dashboard/analytics') },
  ];

  // Funciones reales para workflows
  const handleToggleWorkflow = (id: string) => {
    setWorkflows(prev => prev.map(w => {
      if (w.id === id) {
        const newStatus = w.status === 'active' ? 'paused' : 'active';
        toast.success(`Workflow ${newStatus === 'active' ? 'activado' : 'pausado'} correctamente`);
        return { ...w, status: newStatus };
      }
      return w;
    }));
  };

  const handleExecuteWorkflow = (id: string, name: string) => {
    toast.loading('Ejecutando workflow...', { id: 'execute' });
    
    setTimeout(() => {
      setWorkflows(prev => prev.map(w => 
        w.id === id ? { ...w, executions: w.executions + 1, lastRun: 'Justo ahora' } : w
      ));
      
      toast.success(`Workflow "${name}" ejecutado exitosamente`, { id: 'execute' });
    }, 1500);
  };

  const handleViewWorkflow = (id: string) => {
    navigate(`/dashboard/workflows/${id}`);
  };

  const handleDeleteWorkflow = (id: string, name: string) => {
    if (confirm(`¿Estás seguro de eliminar el workflow "${name}"?`)) {
      setWorkflows(prev => prev.filter(w => w.id !== id));
      toast.success(`Workflow "${name}" eliminado correctamente`);
    }
  };

  // Acciones rápidas funcionales
  const quickActions = [
    {
      label: 'Crear Nuevo Workflow',
      icon: Zap,
      action: () => navigate('/dashboard/workflows/new'),
      variant: 'default' as const,
      description: 'Inicia un nuevo flujo de automatización'
    },
    {
      label: 'Configurar Agente IA',
      icon: Bot,
      action: () => navigate('/dashboard/agents'),
      variant: 'outline' as const,
      description: 'Añade un asistente inteligente'
    },
    {
      label: 'Ver Analytics',
      icon: BarChart3,
      action: () => navigate('/dashboard/analytics'),
      variant: 'outline' as const,
      description: 'Analiza el rendimiento'
    },
    {
      label: 'Marketplace',
      icon: ArrowRight,
      action: () => navigate('/dashboard/marketplace'),
      variant: 'outline' as const,
      description: 'Explora workflows premium'
    },
  ];

  if (isAdmin) {
    quickActions.push({
      label: 'Certificaciones Pendientes',
      icon: CheckCircle2,
      action: () => navigate('/dashboard/assets'),
      variant: 'outline' as const,
      description: 'Revisar aprobaciones'
    });
  }

  return (
    <div className="space-y-8 p-8">
      {/* Onboarding wizard — shown once on first login */}
      {showOnboarding && (
        <OnboardingWizard
          onComplete={handleOnboardingComplete}
          onSkip={handleOnboardingComplete}
        />
      )}
      <div>
        <h1 className="text-3xl mb-2">
          Bienvenido, {user?.name}
        </h1>
        <p className="text-muted-foreground">
          Panel de control de automatización empresarial
        </p>
      </div>

      {/* Stats Cards - AHORA CLICKEABLES */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card 
              key={stat.label} 
              className="relative overflow-hidden border-border hover:shadow-lg transition-all cursor-pointer hover:scale-105"
              onClick={stat.action}
            >
              <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary"></div>
              
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">{stat.label}</CardTitle>
                <Icon className={`h-5 w-5 ${stat.color}`} />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-semibold">{stat.value}</div>
                <p className="text-xs text-muted-foreground mt-1">Click para ver más</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Workflows Recientes - TOTALMENTE FUNCIONAL */}
        <Card className="relative overflow-hidden border-border">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary"></div>
          
          <CardHeader>
            <CardTitle>Workflows Recientes</CardTitle>
            <CardDescription>Tus automatizaciones más activas</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {workflows.map((workflow) => (
              <div 
                key={workflow.id} 
                className="flex items-center justify-between p-4 rounded-lg border border-border hover:bg-accent/50 transition-colors"
              >
                <div className="flex items-center gap-3 flex-1">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 border border-primary/20">
                    <Workflow className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">{workflow.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {workflow.executions} ejecuciones • {workflow.lastRun}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={workflow.status === 'active' ? 'default' : 'secondary'} className="text-xs">
                    {workflow.status === 'active' ? 'Activo' : 'Pausado'}
                  </Badge>
                  
                  {/* Botones funcionales */}
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => handleToggleWorkflow(workflow.id)}
                    title={workflow.status === 'active' ? 'Pausar' : 'Activar'}
                  >
                    {workflow.status === 'active' ? (
                      <Pause className="h-4 w-4" />
                    ) : (
                      <Play className="h-4 w-4" />
                    )}
                  </Button>
                  
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => handleExecuteWorkflow(workflow.id, workflow.name)}
                    title="Ejecutar ahora"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                  
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => handleViewWorkflow(workflow.id)}
                    title="Ver detalles"
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                  
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => handleDeleteWorkflow(workflow.id, workflow.name)}
                    title="Eliminar"
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
            <Button variant="outline" className="w-full mt-2" onClick={() => navigate('/dashboard/workflows')}>
              Ver todos los workflows
            </Button>
          </CardContent>
        </Card>

        {/* Acciones Rápidas - TOTALMENTE FUNCIONAL */}
        <Card className="relative overflow-hidden border-border">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary"></div>
          
          <CardHeader>
            <CardTitle>Acciones Rápidas</CardTitle>
            <CardDescription>Inicia tareas comunes rápidamente</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {quickActions.map((action, index) => {
              const Icon = action.icon;
              return (
                <Button 
                  key={index}
                  className={`w-full justify-start ${index === 0 ? 'bg-primary hover:bg-primary/90 text-white' : ''}`}
                  variant={action.variant}
                  onClick={action.action}
                >
                  <Icon className="mr-2 h-4 w-4" />
                  <div className="flex flex-col items-start">
                    <span>{action.label}</span>
                    <span className="text-xs opacity-70">{action.description}</span>
                  </div>
                </Button>
              );
            })}
          </CardContent>
        </Card>
      </div>

      {/* Actividad Reciente */}
      <Card className="relative overflow-hidden border-border">
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary"></div>
        
        <CardHeader>
          <CardTitle>Actividad Reciente</CardTitle>
          <CardDescription>Últimas ejecuciones y eventos del sistema</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[
              { 
                type: 'success', 
                message: 'Workflow "Envío de Ofertas" ejecutado correctamente',
                time: '5 min ago',
                action: () => navigate('/dashboard/workflows/1')
              },
              { 
                type: 'info', 
                message: 'Nuevo agente IA "Asistente de Ventas" creado',
                time: '1 hour ago',
                action: () => navigate('/dashboard/agents')
              },
              { 
                type: 'success', 
                message: '284 emails enviados automáticamente',
                time: '2 hours ago',
                action: () => navigate('/dashboard/mail')
              },
            ].map((activity, i) => (
              <div 
                key={i} 
                className="flex items-start gap-4 pb-4 border-b border-border last:border-0 last:pb-0 cursor-pointer hover:bg-accent/50 p-2 rounded-lg transition-colors"
                onClick={activity.action}
              >
                <div className={`flex h-9 w-9 items-center justify-center rounded-full ${
                  activity.type === 'success' ? 'bg-green-500/10 text-green-600 dark:text-green-400' : 'bg-blue-500/10 text-blue-600 dark:text-blue-400'
                }`}>
                  <CheckCircle2 className="h-4 w-4" />
                </div>
                <div className="flex-1 space-y-1">
                  <p className="text-sm font-medium">{activity.message}</p>
                  <p className="text-xs text-muted-foreground">{activity.time}</p>
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Admin Analytics Dashboard */}
      {isAdmin && (
        <Card className="relative overflow-hidden border-border">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary"></div>
          
          <CardHeader>
            <CardTitle>Dashboard de Análisis Administrativos</CardTitle>
            <CardDescription>Visualiza datos clave de tu organización</CardDescription>
          </CardHeader>
          <CardContent>
            <AdminAnalyticsDashboard />
          </CardContent>
        </Card>
      )}
    </div>
  );
}